"use client"

// biome-ignore lint/performance/noNamespaceImport: Radix UI Tabs primitive
import * as TabsPrimitive from "@radix-ui/react-tabs"
import { ChevronLeftIcon, ChevronRightIcon } from "lucide-react"
import type * as React from "react"
import { useCallback, useEffect, useRef, useState } from "react"
import { cn } from "~/utilities"

function Tabs({
  className,
  ...props
}: React.ComponentProps<typeof TabsPrimitive.Root>) {
  return (
    <TabsPrimitive.Root
      className={cn("flex flex-col", className)}
      data-slot='tabs'
      {...props}
    />
  )
}

function TabsList({
  className,
  ...props
}: React.ComponentProps<typeof TabsPrimitive.List>) {
  const scrollContainerRef = useRef<HTMLDivElement>(null)
  const [canScrollLeft, setCanScrollLeft] = useState(false)
  const [canScrollRight, setCanScrollRight] = useState(false)
  const [showNavigation, setShowNavigation] = useState(false)
  const [isDragging, setIsDragging] = useState(false)
  const [dragStart, setDragStart] = useState(0)
  const [scrollStart, setScrollStart] = useState(0)

  const SCROLL_BUFFER = 1 // Buffer pixel para evitar imprecisão de float

  const updateScrollPosition = useCallback(() => {
    const container = scrollContainerRef.current

    if (!container) return

    const { scrollLeft, scrollWidth, clientWidth } = container

    const hasOverflow = scrollWidth > clientWidth
    const isAtStart = scrollLeft === 0
    const isAtEnd = scrollLeft >= scrollWidth - clientWidth - SCROLL_BUFFER

    setCanScrollLeft(!isAtStart)
    setCanScrollRight(!isAtEnd)
    setShowNavigation(hasOverflow)
  }, [])

  useEffect(() => {
    const container = scrollContainerRef.current

    if (!container) return

    updateScrollPosition()

    const resizeObserver = new ResizeObserver(updateScrollPosition)
    resizeObserver.observe(container)

    container.addEventListener("scroll", updateScrollPosition)

    return () => {
      resizeObserver.disconnect()
      container.removeEventListener("scroll", updateScrollPosition)
    }
  }, [updateScrollPosition])

  const handleMoveScroll = (direction: "left" | "right") => {
    const container = scrollContainerRef.current
    if (!container) return

    const scrollAmount = 150
    const newScrollPosition =
      direction === "left"
        ? container.scrollLeft - scrollAmount
        : container.scrollLeft + scrollAmount

    container.scrollTo({
      left: newScrollPosition,
      behavior: "smooth",
    })
  }

  const handlePointerDown = (event: React.PointerEvent<HTMLDivElement>) => {
    event.currentTarget.setPointerCapture(event.pointerId)
    setIsDragging(true)
    setDragStart(event.clientX)
    setScrollStart(scrollContainerRef.current?.scrollLeft ?? 0)
  }

  const handlePointerMove = (event: React.PointerEvent<HTMLDivElement>) => {
    if (!(isDragging && scrollContainerRef.current)) return

    const dragDistance = event.clientX - dragStart
    const newScrollPosition = scrollStart - dragDistance

    scrollContainerRef.current.scrollLeft = newScrollPosition
  }

  const handlePointerUp = (event: React.PointerEvent<HTMLDivElement>) => {
    event.currentTarget.releasePointerCapture(event.pointerId)
    setIsDragging(false)
  }

  return (
    <div
      className='relative flex w-full items-center overflow-hidden'
      data-slot='tabs-list-wrapper'
    >
      {showNavigation && canScrollLeft && (
        <button
          aria-label='Rolar para esquerda'
          className='absolute top-0 bottom-0 left-0 z-10 inline-flex w-12 items-center justify-start bg-linear-to-r from-background to-transparent pl-2 text-foreground/60 transition-colors hover:text-foreground md:hidden'
          onClick={() => handleMoveScroll("left")}
          type='button'
        >
          <ChevronLeftIcon className='text-primary hover:text-primary/80' />
        </button>
      )}

      <div
        className={cn(
          "scrollbar-hide flex-1 overflow-x-auto",
          isDragging && "cursor-grabbing"
        )}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        ref={scrollContainerRef}
        style={{ scrollbarWidth: "none", msOverflowStyle: "none" }}
      >
        <TabsPrimitive.List
          className={cn(
            "inline-flex h-auto w-full min-w-max justify-start gap-0 border-border border-b bg-transparent p-0",
            showNavigation && "px-10",
            isDragging && "select-none",
            className
          )}
          data-slot='tabs-list'
          {...props}
        />
      </div>

      {showNavigation && canScrollRight && (
        <button
          aria-label='Rolar para direita'
          className='absolute top-0 right-0 bottom-0 z-10 inline-flex w-12 items-center justify-end bg-linear-to-l from-background to-transparent pr-2 text-foreground/60 transition-colors hover:text-foreground md:hidden'
          onClick={() => handleMoveScroll("right")}
          type='button'
        >
          <ChevronRightIcon className='text-primary hover:text-primary/80' />
        </button>
      )}
    </div>
  )
}

function TabsTrigger({
  className,
  ...props
}: React.ComponentProps<typeof TabsPrimitive.Trigger>) {
  return (
    <TabsPrimitive.Trigger
      className={cn(
        "relative inline-flex items-center justify-center gap-1.5 border-b-2 border-b-transparent px-4 py-2.5 font-medium text-muted-foreground text-sm transition-colors hover:text-foreground/80 focus-visible:outline-1 focus-visible:outline-ring disabled:pointer-events-none disabled:opacity-50 data-[state=active]:border-b-primary data-[state=active]:text-foreground",
        className
      )}
      data-slot='tabs-trigger'
      {...props}
    />
  )
}

function TabsContent({
  className,
  ...props
}: React.ComponentProps<typeof TabsPrimitive.Content>) {
  return (
    <TabsPrimitive.Content
      className={cn("mt-4 flex-1 outline-none", className)}
      data-slot='tabs-content'
      {...props}
    />
  )
}

export { Tabs, TabsList, TabsTrigger, TabsContent }
